import React from 'react';
import ServicePage from '../../components/site/ServicePage';
import Sources from '../../components/site/Sources';
import { SEARCH_FAQS as FAQS } from '../../site/faqs';
import { CONTACT } from '../../site/nav';
import { SOURCES } from '../../site/sources';

const Cite = ({ id }) => (
  <a href={SOURCES[id].href} target="_blank" rel="noopener" className="text-blue-600 hover:text-slate-900 underline underline-offset-4 decoration-blue-400/40">{SOURCES[id].name}</a>
);

// The AI search page follows the research brief: results, not technology.
// Stats come from src/site/sources.js and nowhere else.
export default function AiSearch() {
  return (
    <ServicePage
      path="/ai-search"
      eyebrow="AI search"
      title="Your next customer is asking AI where to buy a sofa. Will it name your store?"
      lead="Shoppers ask AI where to buy furniture, and AI picks which stores to show. Then they decide on your site. We build your site to win both."
      proof={{ label: 'Get a free AI visibility check', href: CONTACT.calendlyUrl }}
      features={[
        { title: 'AI can read your whole catalog', text: 'Every size, colour, material, price, and delivery area.' },
        { title: 'Pages that answer the question', text: 'Each page opens with the answer the shopper is looking for.' },
        { title: 'A page for every city you deliver to', text: 'Local buyers search by town. You have a page for each one.' },
        { title: 'Buying guides', text: 'The questions shoppers ask, answered on your site.' },
        { title: 'Photos and measurements first', text: 'Furniture shoppers look at the picture first and need exact dimensions. Both are on every piece.' },
        { title: 'Checked before it goes live', text: 'We measure how much of your site AI can read, and fix what it cannot.' },
      ]}
      customer={{
        title: 'Two moments. You have to win both.',
        items: [
          { title: 'AI finds you', text: 'When a shopper asks for a grey sectional near them, AI can read your catalog and recommend your store.' },
          { title: 'The shopper chooses you', text: 'They arrive knowing what they want. Big photos, real measurements, and a designer that answers.' },
          { title: 'One catalog does both', text: 'The catalog AI reads is the one your shoppers see.' },
        ],
      }}
      steps={[
        { title: 'Included with the AI sales platform', text: 'Every site we build is ready for AI on day one.' },
        { title: 'Your catalog writes the pages', text: 'Products, collections, cities, and guides come from your data.' },
        { title: 'Measured before and after', text: 'You see how much of your site AI could read before, and how much it reads now.' },
      ]}
      faqs={FAQS}
      closing={{ title: 'Find out what AI sees on your site today.', text: 'Book 30 minutes for a free visibility check.' }}
    >
      <section className="py-10 md:py-16 px-4 border-t border-slate-200">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-center mb-3">Shoppers ask AI first.</h2>
          <p className="text-slate-500 text-center max-w-2xl mx-auto mb-8 md:mb-12">Then they decide on your site.</p>
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)] p-5 md:p-7">
              <p className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">393%</p>
              <p className="text-slate-900 font-semibold mb-1.5">more shoppers sent to US retail sites by AI</p>
              <p className="text-slate-500 text-sm leading-relaxed">Year over year in early 2026, according to <Cite id="S1" />.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)] p-5 md:p-7">
              <p className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">42%</p>
              <p className="text-slate-900 font-semibold mb-1.5">better conversion from shoppers AI sends</p>
              <p className="text-slate-500 text-sm leading-relaxed">March 2026, compared with other visitors, according to <Cite id="S1" />.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-16px_rgba(15,23,42,0.14)] p-5 md:p-7">
              <p className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">15 to 25%</p>
              <p className="text-slate-900 font-semibold mb-1.5">of US online shopping could run through AI agents by 2030</p>
              <p className="text-slate-500 text-sm leading-relaxed">A 2026 estimate from <Cite id="S3" />. Shoppers still make the final call themselves.</p>
            </div>
          </div>
          <div className="mt-6 md:mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 md:p-7 text-center">
            <p className="text-slate-900 text-lg md:text-xl font-bold mb-1.5">More than half of websites look almost blank to AI.</p>
            <p className="text-slate-500 text-sm md:text-base max-w-2xl mx-auto">
              In a 2026 test of the top 1,000 sites, 57% showed a nearly empty page to an AI visitor, according to a <Cite id="S5" />. Adobe found even the best retail sites are only partly readable to AI, with scores from 54% to 83% in 2026, per <Cite id="S4" />.
            </p>
          </div>
        </div>
      </section>
      <SourcesBlock />
    </ServicePage>
  );
}

function SourcesBlock() {
  return <Sources ids={['S1', 'S3', 'S4', 'S5', 'S7', 'S8']} />;
}
